import React, { useEffect, useRef } from "react";
import {
  addNewDoc,
  getAllDocsSnap,
  setDocData,
  deleteDocById,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import {
  FiDroplet,
  FiClock,
  FiCalendar,
  FiCheck,
  FiTrash2,
  FiEdit,
  FiSend,
  FiFilter,
  FiTrendingUp,
  FiDownload,
  FiSearch,
  FiUpload,
  FiXCircle,
  FiFile,
  FiSave,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { unitNumberLabel } from "../../../utils/constants";

function WaterMeterReading() {
  const currentUser = useSelector((state) => state.userObject);
  const [addMode, setAddMode] = React.useState(false);
  const [inProcess, setInProcess] = React.useState(false);
  const [billDate, setBillDate] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [lots, setLots] = React.useState([]);
  const [billRuns, setBillRuns] = React.useState([]);
  const [billRunSelected, setBillRunSelected] = React.useState({});
  const [meterReadings, setMeterReadings] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [numberOfItemsPerPage] = React.useState(5);
  const [activeTab, setActiveTab] = React.useState("All");
  const [selectedYear, setSelectedYear] = React.useState(
    new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = React.useState(
    new Date().getMonth()
  );
  const [filteredReadings, setFilteredReadings] = React.useState([]);
  const [readingsWithConsumption, setReadingsWithConsumption] = React.useState(
    []
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);
  const [uploadMode, setUploadMode] = React.useState(false);
  const [csvData, setCsvData] = React.useState([]);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = useRef(null);
  const [editMode, setEditMode] = React.useState(false);
  const [selectedReading, setSelectedReading] = React.useState(null);
  const [formData, setFormData] = React.useState({
    unitNumber: "",
    meterReading: "",
    dateTime: "",
    notes: "",
  });

  // Dynamically generate years array: current year and 3 years prior
  const currentYear = new Date().getFullYear();
  const years = [
    currentYear - 3,
    currentYear - 2,
    currentYear - 1,
    currentYear,
  ];

  // Available months for selection
  const months = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  useEffect(() => {
    const fetchMeterReadings = async () => {
      await getAllDocsSnap("meterReading", (data) => {
        data.sort((a, b) => {
          const dateA = new Date(a.dateTime);
          const dateB = new Date(b.dateTime);
          return dateB - dateA; // Sort in descending order
        });

        setMeterReadings(data);
        setLoading(false);
      });
    };
    fetchMeterReadings();
  }, []);

  // Filter readings based on selected year and month and calculate consumption
  useEffect(() => {
    if (meterReadings.length > 0) {
      // Filter readings for the selected month/year
      const filtered = meterReadings.filter((reading) => {
        const readingDate = new Date(reading.dateTime);
        return (
          readingDate.getFullYear() === selectedYear &&
          readingDate.getMonth() === selectedMonth
        );
      });
      setFilteredReadings(filtered);

      // Calculate consumption by comparing with previous month readings
      const withConsumption = filtered.map((reading) => {
        // Get previous month's date
        let prevMonthDate = new Date(selectedYear, selectedMonth - 1);

        // If January, go to December of previous year
        if (selectedMonth === 0) {
          prevMonthDate = new Date(selectedYear - 1, 11);
        }

        // Find previous month's reading for this unit
        const prevMonthReading = meterReadings.find((prevReading) => {
          const prevDate = new Date(prevReading.dateTime);
          return (
            prevReading.unitNumber.toLowerCase() ===
              reading.unitNumber.toLowerCase() &&
            prevDate.getMonth() === prevMonthDate.getMonth() &&
            prevDate.getFullYear() === prevMonthDate.getFullYear()
          );
        });

        // Calculate consumption or set to null if no previous reading
        const consumption = prevMonthReading
          ? Number(reading.meterReading) - Number(prevMonthReading.meterReading)
          : null;

        // Add a consumption indicator (up, down, or neutral)
        let trend = "neutral";
        if (consumption !== null) {
          trend = consumption > 0 ? "up" : consumption < 0 ? "down" : "neutral";
        }

        return {
          ...reading,
          consumption: consumption !== null ? consumption : "N/A",
          prevReading: prevMonthReading ? prevMonthReading.meterReading : "N/A",
          trend,
        };
      });

      setReadingsWithConsumption(withConsumption);
      // Reset to first page when filter changes
      setCurrentPage(1);
    }
  }, [meterReadings, selectedYear, selectedMonth]);

  // Handle search functionality
  useEffect(() => {
    if (searchTerm.trim() === "") {
      // If search is empty, show all readings
      setSearchResults(readingsWithConsumption);
    } else {
      // Filter readings based on unit number
      const results = readingsWithConsumption.filter((reading) =>
        reading.unitNumber.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(results);
      // Reset to first page when search changes
      setCurrentPage(1);
    }
  }, [searchTerm, readingsWithConsumption]);

  const handleGenerateBills = () => {
    setAddMode(!addMode);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-eleghoBlue"></div>
      </div>
    );
  }

  const lotMap = lots.map((lot) => {
    return {
      identifier: lot.identifier,
      owner: lot.owner1,
      lotSize: lot.lotSize,
      waterReading: "",
      billAmount: "",
    };
  });

  const handleCreateBillRun = async () => {
    setInProcess(true);
    const docObject = {
      billDate,
      lots: lotMap,
      status: "Draft",
      createdAt: new Date().toLocaleString(),
    };

    await addNewDoc("waterBills", docObject);
    toast.success("Bill Run Created Successfully");
    setInProcess(false);
  };

  const handleEditBillRun = (selected) => {
    setBillRunSelected(selected);
  };

  const handleComputeBill = (reading) => {
    const waterReading = parseFloat(reading);
    let billAmount = 0;

    if (waterReading <= 30) {
      billAmount = waterReading * 20;
    } else if (waterReading <= 40) {
      billAmount = waterReading * 22;
    } else if (waterReading <= 50) {
      billAmount = waterReading * 24;
    } else if (waterReading <= 60) {
      billAmount = waterReading * 26;
    } else if (waterReading <= 70) {
      billAmount = waterReading * 28;
    } else if (waterReading <= 80) {
      billAmount = waterReading * 30;
    } else if (waterReading <= 90) {
      billAmount = waterReading * 32;
    } else if (waterReading <= 100) {
      billAmount = waterReading * 34;
    } else if (waterReading > 100) {
      billAmount = waterReading * 36;
    } else {
      // Default case, which usually should not occur since reading is a number
      billAmount = 0;
    }

    return billAmount;
  };

  const handleWaterReadingChange = (lot, value) => {
    lot.waterReading = value;
    lot.billAmount = handleComputeBill(value);
    setLots([...lots]);
  };

  const handleSaveBillRun = async () => {
    setInProcess(true);
    const docObject = {
      ...billRunSelected,
      status: "Saved",
      savedAt: new Date().toLocaleString(),
      createdBy: "Admin",
    };

    await setDocData("waterBills", billRunSelected.id, docObject);
    setInProcess(false);
    toast.success("Bill Run Updated Successfully");
  };

  const handleSendBillings = async () => {
    setInProcess(true);
    for (let i = 0; i < billRunSelected.lots.length; i++) {
      const lot = billRunSelected.lots[i];
      const docObject = {
        identifier: lot.identifier,
        billingType: "Water",
        description: "Water Bill",
        //due date is 15 days after the run date
        dueDate: new Date(
          new Date(billRunSelected.billDate).getTime() +
            15 * 24 * 60 * 60 * 1000
        ).toLocaleString(),
        owner: lot.owner,
        billDate: billRunSelected.billDate,
        amount: parseFloat(lot.billAmount).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        status: "Unpaid",
        createdAt: new Date().toLocaleString(),
      };

      await addNewDoc("billings", docObject);
    }
    setInProcess(false);
    toast.success("Billings Sent Successfully");
    setBillRunSelected({});
  };

  const handleDelete = async (billRun) => {
    if (window.confirm("Are you sure you want to delete this bill run?")) {
      setInProcess(true);
      await setDocData("waterBills", billRun.id, { status: "Deleted" });
      setInProcess(false);
      toast.success("Bill Run Deleted Successfully");
    }
  };

  const handleReminder = async () => {
    toast.success("Reminders Sent Successfully");
  };

  // Function to download meter readings as Excel
  const handleExportToExcel = () => {
    // Prepare data for export
    const dataToExport = readingsWithConsumption.map((reading) => ({
      Date: new Date(reading.dateTime).toLocaleDateString(),
      Unit_Number: reading.unitNumber,
      Current_Reading: reading.meterReading,
      Previous_Reading: reading.prevReading,
      Consumption: reading.consumption,
      Month: months[selectedMonth].label,
      Year: selectedYear,
    }));

    // Convert data to CSV format
    const headers = Object.keys(dataToExport[0]).join(",");
    const csvRows = dataToExport.map((row) =>
      Object.values(row)
        .map((value) =>
          typeof value === "string" && value.includes(",")
            ? `"${value}"`
            : value
        )
        .join(",")
    );
    const csvContent = [headers, ...csvRows].join("\n");

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Water_Readings_${months[selectedMonth].label}_${selectedYear}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Excel file downloaded successfully");
  };

  // Function to download template CSV for batch upload
  const handleDownloadTemplate = () => {
    // Define the headers for the template based on the data structure
    const headers = ["unitNumber", "meterReading", "dateTime", "notes"];

    // Create an empty row as example with the current date
    const exampleRow = [
      "A101", // unit number example
      "100", // meter reading example
      new Date().toISOString().split("T")[0], // today's date as YYYY-MM-DD
      "Initial reading", // example note
    ];

    // Create CSV content
    const csvContent = [headers.join(","), exampleRow.join(",")].join("\n");

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "water_meter_readings_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV template downloaded successfully");
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    console.log("File selected:", file);

    if (!file) {
      toast.error("No file selected");
      return;
    }

    // Check if it's a CSV file
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    toast.loading("Processing CSV file...");

    // Reset states
    setCsvData([]);
    setValidationErrors([]);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        console.log("File loaded successfully");
        toast.dismiss();

        const content = e.target.result;
        const lines = content.split("\n");

        if (lines.length <= 1) {
          toast.error("CSV file is empty or contains only headers");
          return;
        }

        // Validate header line
        const headers = lines[0].split(",").map((header) => header.trim());
        const requiredHeaders = [
          "unitNumber",
          "meterReading",
          "dateTime",
          "notes",
        ];

        // Check if all required headers are present
        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );

        if (missingHeaders.length > 0) {
          setValidationErrors([
            `Missing required headers: ${missingHeaders.join(", ")}`,
          ]);
          toast.error("CSV file is missing required headers");
          setUploadMode(true);
          return;
        }

        // Parse and validate data
        const parsedData = [];
        const errors = [];

        // Start from index 1 to skip the header row
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue; // Skip empty lines

          const values = lines[i].split(",").map((value) => value.trim());

          // Check if row has correct number of columns
          if (values.length !== headers.length) {
            errors.push(`Row ${i + 1}: Incorrect number of columns`);
            continue;
          }

          // Create object mapping headers to values
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index];
          });

          // Validate required fields
          if (!rowData.unitNumber) {
            errors.push(`Row ${i + 1}: ${unitNumberLabel} is required`);
          }
          if (
            !rowData.meterReading ||
            isNaN(parseFloat(rowData.meterReading))
          ) {
            errors.push(`Row ${i + 1}: Meter reading must be a valid number`);
          }
          if (!rowData.dateTime || !isValidDate(rowData.dateTime)) {
            errors.push(`Row ${i + 1}: Date must be a valid date (YYYY-MM-DD)`);
          }

          // If no errors for this row, add to parsed data
          if (!errors.some((error) => error.includes(`Row ${i + 1}:`))) {
            parsedData.push(rowData);
          }
        }

        // Set validation errors if any
        if (errors.length > 0) {
          setValidationErrors(errors);
          toast.error(`Found ${errors.length} errors in the CSV file`);
        }

        // Set parsed data for preview
        setCsvData(parsedData);

        console.log("Parsed data:", parsedData);
        console.log("Validation errors:", errors);

        // Open upload modal if data is valid or if there are validation errors to show
        if (parsedData.length > 0 || errors.length > 0) {
          setUploadMode(true);
          if (parsedData.length > 0) {
            toast.success(`Found ${parsedData.length} valid meter readings`);
          }
        } else {
          toast.error("No valid data found in the CSV file");
        }
      } catch (error) {
        console.error("Error processing CSV:", error);
        toast.error("Error processing CSV file: " + error.message);
      }
    };

    reader.onerror = (error) => {
      console.error("File read error:", error);
      toast.error("Error reading file");
    };

    try {
      reader.readAsText(file);
    } catch (error) {
      console.error("Error initiating file read:", error);
      toast.error("Error reading file: " + error.message);
    }

    // Reset file input so the same file can be selected again
    event.target.value = null;
  };

  const isValidDate = (dateString) => {
    // Check if date is in YYYY-MM-DD format
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    // Check if date is valid
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const handleSaveReadings = async () => {
    if (csvData.length === 0) {
      toast.error("No valid data to import");
      return;
    }

    setIsUploading(true);

    // Track successful and failed uploads
    let successCount = 0;
    let failedCount = 0;

    try {
      // Process each meter reading entry
      for (const reading of csvData) {
        try {
          // Format data for Firebase
          const readingData = {
            unitNumber: reading.unitNumber,
            meterReading: reading.meterReading,
            dateTime: reading.dateTime,
            notes: reading.notes || "",
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
            createdByName: currentUser.displayName,
            imageUrl: reading.imageUrl || "",
            status: "saved",
          };

          // Add to Firebase
          await addNewDoc("meterReading", readingData);
          successCount++;
        } catch (error) {
          console.error("Error adding reading:", error);
          failedCount++;
        }
      }

      // Show success message
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} meter readings`);

        // Refresh readings list
        await getAllDocsSnap("meterReading", (data) => {
          data.sort((a, b) => {
            const dateA = new Date(a.dateTime);
            const dateB = new Date(b.dateTime);
            return dateB - dateA; // Sort in descending order
          });
          setMeterReadings(data);
        });
      }

      // Show error message if any failed
      if (failedCount > 0) {
        toast.error(`Failed to import ${failedCount} meter readings`);
      }

      // Close modal and reset states
      setUploadMode(false);
      setCsvData([]);
      setValidationErrors([]);
    } catch (error) {
      console.error("Error during bulk import:", error);
      toast.error("An error occurred during import");
    } finally {
      setIsUploading(false);
    }
  };

  // Filter readings based on active tab
  const startIndex = (currentPage - 1) * numberOfItemsPerPage;
  const endIndex = startIndex + numberOfItemsPerPage;
  // Use search results if searching, otherwise use all readings with consumption
  const displayedReadings =
    searchResults.length > 0 || searchTerm.trim() !== ""
      ? searchResults.slice(startIndex, endIndex)
      : readingsWithConsumption.slice(startIndex, endIndex);

  // Calculate total consumption for the period
  const totalConsumption = readingsWithConsumption
    .filter((reading) => reading.consumption !== "N/A")
    .reduce((sum, reading) => sum + Number(reading.consumption), 0);

  // Find highest consumption unit
  const highestConsumption =
    readingsWithConsumption.length > 0
      ? [...readingsWithConsumption].sort((a, b) => {
          if (a.consumption === "N/A") return 1;
          if (b.consumption === "N/A") return -1;
          return b.consumption - a.consumption;
        })[0]
      : null;

  // Stats cards data
  const stats = {
    totalReadings: filteredReadings.length,
    totalConsumption: totalConsumption,
    highestUnit: highestConsumption ? highestConsumption.unitNumber : "N/A",
    highestConsumption:
      highestConsumption && highestConsumption.consumption !== "N/A"
        ? highestConsumption.consumption
        : 0,
    pendingBills: billRuns.filter((bill) => bill.status === "Draft").length,
  };

  // Handle edit button click
  const handleEdit = (reading) => {
    setSelectedReading(reading);
    setFormData({
      unitNumber: reading.unitNumber,
      meterReading: reading.meterReading,
      dateTime: reading.dateTime ? reading.dateTime.split("T")[0] : "",
      notes: reading.notes || "",
    });
    setEditMode(true);
  };

  // Handle delete button click with confirmation
  const handleDeleteReading = async (reading) => {
    if (
      window.confirm(
        `Are you sure you want to delete the reading for unit ${reading.unitNumber}?`
      )
    ) {
      try {
        setInProcess(true);
        await deleteDocById("meterReading", reading.id);
        toast.success("Reading deleted successfully");

        // Update the readings list
        const updatedReadings = meterReadings.filter(
          (item) => item.id !== reading.id
        );
        setMeterReadings(updatedReadings);
        setInProcess(false);
      } catch (error) {
        console.error("Error deleting reading:", error);
        toast.error("Failed to delete reading");
        setInProcess(false);
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    if (!formData.unitNumber || !formData.meterReading || !formData.dateTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (isNaN(parseFloat(formData.meterReading))) {
      toast.error("Meter reading must be a valid number");
      return;
    }

    try {
      setInProcess(true);

      // Update the document in Firebase
      await setDocData("meterReading", selectedReading.id, {
        ...selectedReading,
        unitNumber: formData.unitNumber,
        meterReading: formData.meterReading,
        dateTime: formData.dateTime,
        notes: formData.notes,
        updatedAt: new Date().toISOString(),
      });

      // Update the reading in the local state
      const updatedReadings = meterReadings.map((reading) => {
        if (reading.id === selectedReading.id) {
          return {
            ...reading,
            unitNumber: formData.unitNumber,
            meterReading: formData.meterReading,
            dateTime: formData.dateTime,
            notes: formData.notes,
            updatedAt: new Date().toISOString(),
          };
        }
        return reading;
      });

      setMeterReadings(updatedReadings);
      toast.success("Reading updated successfully");
      setEditMode(false);
      setSelectedReading(null);
      setInProcess(false);
    } catch (error) {
      console.error("Error updating reading:", error);
      toast.error("Failed to update reading");
      setInProcess(false);
    }
  };

  return (
    <div className="text-xs">
      <div className="mt-4">
        <div className="font-bold text-eleghoBlue uppercase text-left text-sm mb-6">
          Water Meter Readings Management
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-eleghoBlue/10 rounded-lg">
                <FiDroplet className="w-5 h-5 text-eleghoBlue" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Readings</div>
                <div className="text-2xl font-semibold text-eleghoBlue">
                  {stats.totalReadings}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-eleghoGreen/10 rounded-lg">
                <FiTrendingUp className="w-5 h-5 text-eleghoGreen" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Consumption</div>
                <div className="text-2xl font-semibold text-eleghoGreen">
                  {stats.totalConsumption}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <FiClock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Highest Consumer</div>
                <div className="text-xl font-semibold text-yellow-500">
                  {stats.highestUnit} ({stats.highestConsumption})
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <FiCalendar className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Pending Bills</div>
                <div className="text-2xl font-semibold text-red-500">
                  {stats.pendingBills}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Date Filter Panel */}
          <div className="w-full md:w-64 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <div className="font-medium text-eleghoBlue mb-4 flex items-center gap-2">
              <FiFilter className="w-4 h-4" />
              <span>Filter by Period</span>
            </div>

            <div className="mb-4">
              <label className="block text-gray-600 text-xs mb-1">Year</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-600 text-xs mb-1">Month</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="text-gray-600 text-xs mb-2">
                Current Selection:
              </div>
              <div className="text-eleghoBlue font-medium">
                {months[selectedMonth].label} {selectedYear}
              </div>
              <div className="mt-2 text-gray-500 text-xs">
                {filteredReadings.length} readings found
              </div>
            </div>
          </div>

          <div className="flex-1">
            {/* Action buttons */}
            <div className="flex items-center justify-between mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiSearch className="w-4 h-4 text-gray-500" />
                </div>
                <input
                  className="w-64 pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                  placeholder={`Search by ${unitNumberLabel}...`}
                  type="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={clearSearch}
                  >
                    <svg
                      className="w-4 h-4 text-gray-500 hover:text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors"
                  onClick={handleGenerateBills}
                >
                  <FiDroplet className="w-4 h-4" />
                  Generate Water Bills
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  onClick={triggerFileInput}
                >
                  <FiUpload className="w-4 h-4" />
                  Upload Readings
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".csv"
                  onChange={handleFileUpload}
                  onClick={(e) => {
                    // Reset the input value to ensure onChange fires even if the same file is selected
                    e.target.value = null;
                  }}
                />
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-eleghoGreen text-white rounded-lg hover:bg-eleghoGreen/90 transition-colors"
                  onClick={handleReminder}
                >
                  <FiSend className="w-4 h-4" />
                  Send Reminders
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex space-x-4 mb-6">
              {["All", "Recent", "High Usage", "Low Usage"].map((tab) => (
                <div
                  key={tab}
                  className={`px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    activeTab === tab
                      ? "bg-eleghoBlue text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </div>
              ))}
            </div>

            {/* Bill Date Input (shown only when addMode is true) */}
            {addMode && (
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
                <div className="text-sm font-medium text-eleghoBlue mb-2">
                  Select Bill Period
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                    name="billDate"
                    onChange={(e) => setBillDate(e.target.value)}
                  />
                  <button
                    className={`px-4 py-2 rounded-lg text-white ${
                      inProcess
                        ? "bg-gray-400"
                        : "bg-eleghoBlue hover:bg-eleghoBlue/90"
                    } transition-colors`}
                    onClick={handleCreateBillRun}
                    disabled={inProcess}
                  >
                    {inProcess ? "Processing..." : "Create Bill Run"}
                  </button>
                </div>
              </div>
            )}

            {/* Readings Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-800">
                Meter Readings for {months[selectedMonth].label} {selectedYear}
                {searchTerm && (
                  <span className="ml-2 text-sm text-gray-500">
                    Search results for "{searchTerm}"
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-eleghoGreen text-white hover:bg-eleghoGreen/90"
                >
                  <FiFile className="w-3.5 h-3.5" />
                  Download Template
                </button>
                <button
                  onClick={handleExportToExcel}
                  disabled={filteredReadings.length === 0}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                    filteredReadings.length === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-eleghoBlue text-white hover:bg-eleghoBlue/90"
                  }`}
                >
                  <FiDownload className="w-3.5 h-3.5" />
                  Export to Excel
                </button>
                <div className="text-gray-500 text-xs">
                  {searchTerm ? searchResults.length : filteredReadings.length}{" "}
                  entries
                </div>
              </div>
            </div>

            {/* Meter Readings Cards */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="grid grid-cols-6 py-3 px-4 bg-gray-50 text-gray-700 font-medium">
                <div>Date</div>
                <div>{unitNumberLabel}</div>
                <div>Current Reading</div>
                <div>Previous Reading</div>
                <div>Consumption</div>
                <div>Actions</div>
              </div>

              <div className="divide-y divide-gray-100">
                {(
                  searchTerm
                    ? searchResults.length > 0
                    : filteredReadings.length > 0
                ) ? (
                  displayedReadings.map((reading, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-6 py-3 px-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className="text-gray-900">
                          {new Date(reading.dateTime).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-gray-700">{reading.unitNumber}</div>
                      <div className="font-medium text-eleghoBlue">
                        {reading.meterReading}
                      </div>
                      <div className="text-gray-600">{reading.prevReading}</div>
                      <div
                        className={`font-medium flex items-center gap-1 ${
                          reading.trend === "up"
                            ? "text-red-500"
                            : reading.trend === "down"
                            ? "text-eleghoGreen"
                            : "text-gray-500"
                        }`}
                      >
                        {reading.consumption === "N/A" ? (
                          <span>N/A</span>
                        ) : (
                          <>
                            {reading.consumption}
                            {reading.trend === "up" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M12 7a1 1 0 01-1-1V3.414l-8.293 8.293a1 1 0 01-1.414-1.414l8.293-8.293H7a1 1 0 010-2h6a1 1 0 011 1v6a1 1 0 01-1 1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                            {reading.trend === "down" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M14 13a1 1 0 00-1 1v2.586l-8.293-8.293a1 1 0 00-1.414 1.414L11.586 18H9a1 1 0 000 2h6a1 1 0 001-1v-6a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1.5 bg-eleghoBlue/10 text-eleghoBlue rounded hover:bg-eleghoBlue/20 transition-colors"
                          onClick={() => handleEdit(reading)}
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition-colors"
                          onClick={() => handleDeleteReading(reading)}
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-12 text-center text-gray-500">
                    {searchTerm
                      ? `No meter readings found for ${unitNumberLabel} "${searchTerm}"`
                      : `No meter readings found for ${months[selectedMonth].label} ${selectedYear}`}
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {(searchTerm
                ? searchResults.length > 0
                : filteredReadings.length > 0) && (
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-100">
                  <div className="text-gray-600 text-sm">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(
                      endIndex,
                      searchTerm
                        ? searchResults.length
                        : filteredReadings.length
                    )}{" "}
                    of{" "}
                    {searchTerm
                      ? searchResults.length
                      : filteredReadings.length}{" "}
                    entries
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className={`p-2 rounded ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-eleghoBlue/10 text-eleghoBlue hover:bg-eleghoBlue/20"
                      } transition-colors`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      disabled={
                        endIndex >= searchTerm
                          ? searchResults.length
                          : filteredReadings.length
                      }
                      className={`p-2 rounded ${
                        endIndex >= searchTerm
                          ? searchResults.length
                          : filteredReadings.length
                          ? "bg-gray-100 text-gray-400"
                          : "bg-eleghoBlue/10 text-eleghoBlue hover:bg-eleghoBlue/20"
                      } transition-colors`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bill Run Selected */}
        {billRunSelected.lots && (
          <div className="mt-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 bg-eleghoBlue text-white">
              <div className="font-bold">
                Bill Run for {billRunSelected.billDate}
              </div>
            </div>

            <div className="p-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-2 text-left">Identifier</th>
                    <th className="p-2 text-left">Owner</th>
                    <th className="p-2 text-left">Unit Size</th>
                    <th className="p-2 text-left">Water Reading</th>
                    <th className="p-2 text-left">Bill Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {billRunSelected.lots.map((lot, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="p-2">{lot.identifier}</td>
                      <td className="p-2">{lot.owner}</td>
                      <td className="p-2">{lot.lotSize}</td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="border border-gray-300 rounded p-1 w-full focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                          name="waterReading"
                          onChange={(e) =>
                            handleWaterReadingChange(lot, e.target.value)
                          }
                          value={lot.waterReading}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="border border-gray-300 rounded p-1 w-full focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                          name="billAmount"
                          value={lot.billAmount}
                          readOnly
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-100 flex items-center gap-2">
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white ${
                  inProcess
                    ? "bg-gray-400"
                    : "bg-eleghoBlue hover:bg-eleghoBlue/90"
                } transition-colors`}
                onClick={handleSaveBillRun}
                disabled={inProcess}
              >
                <FiCheck className="w-4 h-4" />
                {inProcess ? "Processing..." : "Save Bill Run"}
              </button>
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white ${
                  inProcess
                    ? "bg-gray-400"
                    : "bg-eleghoGreen hover:bg-eleghoGreen/90"
                } transition-colors`}
                onClick={handleSendBillings}
                disabled={inProcess}
              >
                <FiSend className="w-4 h-4" />
                {inProcess ? "Processing..." : "Send Billings"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSV Upload Modal */}
      {uploadMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-eleghoBlue">
                CSV Import Preview
              </h2>
              <button
                onClick={() => setUploadMode(false)}
                className="text-gray-500 hover:text-eleghoBlue transition-colors"
              >
                <FiXCircle size={20} />
              </button>
            </div>

            {validationErrors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-red-600 font-medium mb-2">
                  Validation Errors
                </h3>
                <ul className="list-disc pl-5 text-sm text-red-600">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-4">
              <p className="text-gray-700">
                Found {csvData.length} valid meter readings to import. Review
                the data below before confirming.
              </p>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {csvData.length > 0 &&
                      Object.keys(csvData[0]).map((header) => (
                        <th
                          key={header}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {csvData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      {Object.values(row).map((value, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-3 py-2 whitespace-nowrap text-xs text-gray-700"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setUploadMode(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReadings}
                disabled={isUploading || csvData.length === 0}
                className={`px-4 py-2 rounded-lg text-white text-xs flex items-center ${
                  isUploading || csvData.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isUploading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></span>
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4 mr-2" />
                    <span>Import Readings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reading Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-eleghoBlue">
                Edit Meter Reading
              </h2>
              <button
                onClick={() => setEditMode(false)}
                className="text-gray-500 hover:text-eleghoBlue transition-colors"
              >
                <FiXCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {unitNumberLabel}
                  </label>
                  <input
                    type="text"
                    name="unitNumber"
                    value={formData.unitNumber}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meter Reading
                  </label>
                  <input
                    type="number"
                    name="meterReading"
                    value={formData.meterReading}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reading Date
                  </label>
                  <input
                    type="date"
                    name="dateTime"
                    value={formData.dateTime}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
                    rows={3}
                  ></textarea>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inProcess}
                  className={`px-4 py-2 rounded-lg text-white flex items-center ${
                    inProcess
                      ? "bg-gray-400"
                      : "bg-eleghoBlue hover:bg-eleghoBlue/90"
                  }`}
                >
                  {inProcess ? (
                    <>
                      <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4 mr-2" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WaterMeterReading;

// useEffect(() => {
//   const fetchBillRuns = async () => {
//     await getAllDocsSnap("waterBills", (data) => {
//       //filter status !== Deleted
//       const filteredData = data.filter((run) => run.status !== "Deleted");
//       setBillRuns(filteredData);
//     });
//   };
//   fetchBillRuns();
// }, []);

// useEffect(() => {
//   const fetchLots = async () => {
//     await getAllDocsSnap("residents", (data) => {
//       setLots(data);
//       setLoading(false);
//     });
//   };
//   fetchLots();
// }, []);
